import { useContext, useEffect, useState } from 'react';
import { message } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { getCompanyAiReceptionistCallTranscript } from '../../../../domain/use_cases/ai-receptionist/getCompanyAiReceptionistCallTranscript';
import { ReceptionistTranscriptMessage, parseReceptionistTranscript } from '../types/ReceptionistCallDisplay';

// The SRT is left out of the call list (heavy, rarely read): fetched once, the first time the drawer opens
const useReceptionistCallTranscriptController = (callId: string, open: boolean) => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany } = useContext(CompanyContext);

  const [transcript, setTranscript] = useState<{ callId: string; messages: ReceptionistTranscriptMessage[] } | null>(null);

  const [failedCallId, setFailedCallId] = useState<string | null>(null);

  const companyId = activeCompany?.id;
  const isLoaded = transcript?.callId === callId;
  const hasFailed = failedCallId === callId;

  // A failure only lasts while the drawer stays open: reopening it tries again
  if (!open && failedCallId !== null) {
    setFailedCallId(null);
  }

  useEffect(() => {
    if (!open || isLoaded || hasFailed || !companyId || !authentication) return;

    let isCancelled = false;
    getCompanyAiReceptionistCallTranscript({ company_id: companyId, call_id: callId }, authentication)
      .then((response) => {
        if (!isCancelled) setTranscript({ callId, messages: parseReceptionistTranscript(response.transcription_srt) });
      })
      .catch((e) => {
        if (isCancelled) return;
        message.error(e instanceof Error ? e.message : 'Erreur lors du chargement de la transcription');
        setFailedCallId(callId);
      });
    return () => {
      isCancelled = true;
    };
  }, [open, isLoaded, hasFailed, callId, companyId, authentication]);

  return {
    state: {
      messages: isLoaded ? transcript.messages : [],
      isLoading: open && !isLoaded && !hasFailed,
      hasFailed,
    },
  };
};

export default useReceptionistCallTranscriptController;
